import { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Layout } from '../components/layout';

const SITE_URL = 'https://www.patekarecycle.com';

const services = [
  {
    num: '01',
    title: 'Scrapyard & Factory Scrap Management',
    description:
      'Scheduled and on-demand waste pickup for recyclable and non-hazardous materials. Route-optimized pickups, weight-based reporting, organized storage and dispatch.',
    href: '/services/integrated-waste-management',
  },
  {
    num: '02',
    title: 'Plastic Waste Management',
    description:
      'Sorting processes determine recovery value. Manual and mechanical segregation, contamination reduction, grade-based material clustering.',
    href: '/services/recycling-solutions',
  },
  {
    num: '03',
    title: 'Expired Stock Disposal',
    description:
      'Retailers and distributors face large volumes of unsaleable inventory. Inventory assessment, authorized liquidation channels, recycling of packaging materials, secure destruction.',
    href: '/services/resource-recovery',
  },
  {
    num: '04',
    title: 'Liquidation of Stocks',
    description:
      'Dead inventory becomes recoverable value. Reverse logistics and take-back programs for brands and retailers managing post-consumer materials.',
    href: '/services/resource-recovery',
  },
  {
    num: '05',
    title: 'E-Scrap and E-waste Management',
    description:
      'IT equipment, servers, laptops, printers, cables. Dismantling, secure destruction, recycling through authorized partners, compliance documentation.',
    href: '/services/recycling-solutions',
  },
  {
    num: '06',
    title: 'Expired Cosmetics & Pharmaceuticals Waste',
    description:
      'Specialized compliant disposal with proper documentation to meet regulatory requirements.',
    href: '/services/resource-recovery',
  },
  {
    num: '07',
    title: 'EPR Services for Plastic and E-waste',
    description:
      'Plastic EPR execution, collection partnerships, documentation, audit support. Brands need compliance support.',
    href: '/services/compliance-epr',
  },
  {
    num: '08',
    title: 'Scrap Trading',
    description:
      "One factory's waste = another's raw material. Plastic scrap, OCC paper, metal, textile waste. Circular industrial network.",
    href: '/services/resource-recovery',
  },
];

const materials = [
  { label: 'Plastic', detail: 'PET, HDPE, LDPE, MLP' },
  { label: 'Paper and Cardboard', detail: 'OCC, mixed paper, kraft' },
  { label: 'Glass / Wood', detail: 'Cullet, timber offcuts' },
  { label: 'Ferrous & Non-Ferrous Metals', detail: 'MS, Aluminium, Battery Scrap' },
  { label: 'Electronic Waste', detail: 'IT assets, consumer electronics' },
  { label: 'Expired Grocery & Cosmetics', detail: 'FMCG, pharma, personal care' },
];

const processSteps = [
  'Consultation',
  'Waste Assessment',
  'System Design',
  'Implementation',
  'Reporting',
];

function ServicesList() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section ref={ref} className="py-20 bg-gray-950">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-4">
            What We Do
          </p>
          <hr className="border-gray-800" />
        </motion.div>

        <div>
          {services.map((service, index) => (
            <motion.div
              key={service.num}
              initial={{ opacity: 0, y: 12 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.06 }}
            >
              <Link
                to={service.href}
                className="group flex items-start gap-6 lg:gap-10 py-6 border-b border-gray-800 hover:bg-gray-900 transition-colors px-2 -mx-2"
              >
                {/* Number */}
                <span className="text-3xl lg:text-4xl font-bold text-gray-700 group-hover:text-p-600 transition-colors w-14 shrink-0 tabular-nums pt-0.5">
                  {service.num}
                </span>

                {/* Title */}
                <span className="text-base lg:text-xl font-semibold text-white w-64 lg:w-80 shrink-0 leading-snug">
                  {service.title}
                </span>

                {/* Description */}
                <span className="hidden md:block text-sm text-gray-400 flex-1 leading-relaxed pt-0.5">
                  {service.description}
                </span>

                {/* Arrow */}
                <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-p-500 shrink-0 mt-1 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Services() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true });
  const materialsRef = useRef(null);
  const materialsInView = useInView(materialsRef, { once: true, margin: '-60px' });
  const processRef = useRef(null);
  const processInView = useInView(processRef, { once: true, margin: '-60px' });
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, { once: true, margin: '-60px' });

  return (
    <Layout>
      <Helmet>
        <title>Our Services | Punarvaa - Waste Management & Recycling Solutions India</title>
        <meta
          name="title"
          content="Our Services | Punarvaa - Waste Management & Recycling Solutions India"
        />
        <meta
          name="description"
          content="Comprehensive waste management services by Punarvaa — scrap management, plastic waste, e-waste, EPR compliance, expired stock disposal, liquidation, and scrap trading."
        />
        <meta
          name="keywords"
          content="waste management services India, scrap management, plastic waste, e-waste management, EPR services, expired stock disposal, scrap trading, resource recovery"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/services`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services`} />
        <meta property="og:title" content="Our Services | Punarvaa - Waste Management Solutions" />
        <meta
          property="og:description"
          content="Scrap management, plastic waste, e-waste, EPR compliance, expired stock disposal, liquidation, and scrap trading across India."
        />
        <meta property="og:site_name" content="Punarvaa by Patekar Recycle" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@patekarrecycle" />
        <meta name="twitter:title" content="Our Services | Punarvaa - Waste Management Solutions" />
        <meta
          name="twitter:description"
          content="Scrap management, plastic waste, e-waste, EPR compliance, expired stock disposal, liquidation, and scrap trading."
        />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero */}
      <section ref={heroRef} className="pt-32 pb-20 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-p-500 mb-6">
              Punarvaa Services
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-display text-white leading-[0.95] mb-6">
              Integrated Waste Management and Resource Recovery.
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mb-10">
              Punarvaa delivers structured waste management systems designed for modern businesses.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact?audit=true"
                className="px-5 py-2.5 bg-p-600 text-white text-sm font-semibold rounded-full hover:bg-p-700 transition-colors"
              >
                Request Assessment
              </Link>
              <Link
                to="/contact?quote=true"
                className="px-5 py-2.5 border border-gray-700 text-gray-300 text-sm font-semibold rounded-full hover:border-p-600 hover:text-p-400 transition-colors"
              >
                Get Quote
              </Link>
              <Link
                to="/contact"
                className="px-5 py-2.5 border border-gray-700 text-gray-300 text-sm font-semibold rounded-full hover:border-p-600 hover:text-p-400 transition-colors"
              >
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services editorial list */}
      <ServicesList />

      {/* Materials section */}
      <section ref={materialsRef} className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={materialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-3">
              Materials Handled
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              What we recover.
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-800">
            {materials.map((mat, i) => (
              <motion.div
                key={mat.label}
                initial={{ opacity: 0 }}
                animate={materialsInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="bg-gray-900 p-6"
              >
                <p className="text-white font-semibold mb-1">{mat.label}</p>
                <p className="text-sm text-gray-500">{mat.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process strip */}
      <section ref={processRef} className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3">
              How It Works
            </p>
            <h2 className="text-2xl font-bold text-gray-900">Our process.</h2>
          </motion.div>
          <div className="flex flex-wrap items-center gap-0">
            {processSteps.map((step, i) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: -8 }}
                animate={processInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-center gap-0"
              >
                <div className="flex items-center gap-3 px-5 py-3 border border-gray-200 rounded-full">
                  <span className="text-xs font-bold text-p-600 tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-sm font-medium text-gray-800">{step}</span>
                </div>
                {i < processSteps.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-gray-300 mx-1" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="py-20 bg-p-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-8"
          >
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                Need a Custom Solution?
              </h2>
              <p className="text-p-100 text-lg max-w-xl">
                Every business has unique waste management needs. Contact us to discuss how Punarvaa
                can help you achieve your sustainability and compliance goals.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <Link
                to="/contact?quote=true"
                className="px-8 py-4 bg-white text-p-800 font-semibold rounded-xl hover:bg-p-50 transition-colors text-center"
              >
                Get a Quote
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors text-center"
              >
                Talk to Our Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
